/**
 * 『パンドラコール』 (Pandora Call)
 * Music / Lyrics: 廉 / MV Director: 鈴本 蓮 / Title & Typography Design: 岩佐 知昴
 * Official MV: https://youtu.be/mQz2IA56VCo
 *
 * ジェネラティブアート・コンセプト（1シーン完結・インタラクティブ時計機構）:
 * - MVの退廃的で鋭利な空気感（漆黒の傾いた部屋、スポットライトの白い光束、直管蛍光灯、ブラウン管の走査線）をベースに、
 *   最も印象的な「深紅の精密歯車（ギア）」と「逆回転するアンティーク時計の文字盤」を主役に据えた1シーン完結のジェネラティブアート。
 * - タイポグラフィの過剰な主張を抑え、歯車の外周や時計のベゼルに刻まれた繊細な円環刻印として歌詞やモチーフを融合。
 * - 「再生成（Reroll）」ボタンを押すたびに、歯車列の噛み合い構造や時計の配置、および4つのMV空間モード
 *   （0: 紅蓮の噛み合う大歯車と二連古時計, 1: くり抜かれた『世⚙️界』の歯車機構と明滅する裸電球,
 *     2: 黒電話ダイヤルと時計塔の連鎖機構, 3: 深紅反転エクリプス）がランダム／順番に切り替わる。
 * - キャンバスをクリックすると、その場所に新たな深紅の歯車と時計波紋が噛み合って出現し、機構全体に脱進機の衝撃が走る。
 */

export class PandoraGenerator {
  constructor(p) {
    this.p = p;
    this.time = 0;
    this.modeIndex = -1; // 初回initでランダム選択、再生成で切り替え
    this.roomTilt = -0.14;

    // メインの噛み合う歯車ネットワーク
    this.gears = [];
    // アンティーク時計の文字盤群
    this.clocks = [];
    // 傾いた部屋の蛍光管・スポットライト
    this.fluorescentTubes = [];
    // 空間を漂う埃（ダスト）と赤い火花粒子
    this.dustMotes = [];
    // クリックで出現するインタラクティブ歯車＆時計波紋
    this.spawnedGears = [];
    // カーソル軌跡のドラフティング十字・火花
    this.cursorTrail = [];

    // 脱進機（チックタック衝撃）とフラッシュ状態
    this.escapementImpulse = 0;
    this.crimsonPulse = 0;
    this.pointerX = 0;
    this.pointerY = 0;
    this.parallaxX = 0;
    this.parallaxY = 0;

    // 歯車外周に繊細に刻まれる円環テキスト（主張しすぎない刻印）
    this.ringInscriptions = [
      'P A N D O R A   C A L L   *   I S E K A I   J O U C H O   *   ',
      '可笑しな旋律に触れる   精巧な音の罠   まだ貴方は気付かない   ',
      '散々と踊りましょう   偏愛に壊れる貴方の世界   さあ全部忘れましょう   ',
      'misty calling dreaming Violet   deep inside   mistake calling killing parade   ',
      'わたしから貴方へと送るの   生まれ行く災厄を   わたしだけの世界   '
    ];
  }

  onReroll() {
    // 再生成時に次のアーキタイプモードへ切り替え
    this.modeIndex = (this.modeIndex + 1) % 4;
    this.crimsonPulse = 0.85;
  }

  init(params) {
    const p = this.p;
    const w = p.width || 800;
    const h = p.height || 600;
    const density = params ? params.density : 0.5;
    const scale = Math.min(w, h) / 720;

    if (this.modeIndex < 0) {
      this.modeIndex = Math.floor(p.random(4));
    }

    this.pointerX = w * 0.5;
    this.pointerY = h * 0.5;
    this.roomTilt = p.random(-0.18, -0.09);

    this.gears = [];
    this.clocks = [];
    this.fluorescentTubes = [];
    this.dustMotes = [];
    this.spawnedGears = [];
    this.cursorTrail = [];

    // 1. モードに応じた歯車ネットワーク（Gear Train）とアンティーク時計の構築
    this.buildClockworkMechanism(w, h, scale, density);

    // 2. 部屋を貫く白い直管蛍光灯（3〜5本）
    const tubeCount = Math.floor(3 + density * 2);
    for (let i = 0; i < tubeCount; i++) {
      const x1 = p.random(0.08, 0.92);
      const y1 = p.random(0.05, 0.35);
      const len = p.random(0.28, 0.52);
      const ang = this.roomTilt + p.PI * 0.5 + p.random(-0.25, 0.25);
      this.fluorescentTubes.push({
        x1,
        y1,
        x2: x1 + Math.cos(ang) * len,
        y2: y1 + Math.sin(ang) * len,
        thickness: p.random(2.5, 4.5),
        flickerPhase: p.random(p.TWO_PI)
      });
    }

    // 3. スポットライトの中を舞う埃と深紅の火粉
    const dustCount = Math.floor(70 + density * 110);
    for (let i = 0; i < dustCount; i++) {
      const isCrimsonSpark = p.random() < 0.28;
      this.dustMotes.push({
        x: p.random(w),
        y: p.random(h),
        vx: p.random(-0.25, 0.25),
        vy: p.random(-0.55, -0.1),
        size: isCrimsonSpark ? p.random(1.8, 3.8) : p.random(1.0, 2.6),
        alpha: p.random(60, 200),
        isRed: isCrimsonSpark,
        phase: p.random(p.TWO_PI)
      });
    }
  }

  /**
   * 互いに噛み合って回転する赤い歯車群とアンティーク時計文字盤の生成
   */
  buildClockworkMechanism(w, h, scale, density) {
    const p = this.p;
    const cx = w * 0.5;
    const cy = h * 0.5;

    // 中心となるマスター歯車（深紅の大歯車）
    const masterRadius = p.random(105, 132) * scale;
    const masterX = this.modeIndex === 1 ? w * 0.34 : cx + p.random(-35, 35) * scale;
    const masterY = this.modeIndex === 1 ? h * 0.48 : cy + p.random(-20, 20) * scale;

    this.gears.push({
      x: masterX,
      y: masterY,
      radius: masterRadius,
      teeth: 38,
      angle: p.random(p.TWO_PI),
      speed: 0.42, // 基準角速度
      dir: 1,
      style: 'crimson_main', // 深紅の主歯車
      spokes: 5,
      hasInscription: true,
      inscriptionText: this.ringInscriptions[0],
      depth: 1.0
    });

    // マスター歯車から連鎖的に噛み合う衛星歯車群を配置（7〜13個）
    const totalGears = Math.floor(8 + density * 6);
    let attempts = 0;

    while (this.gears.length < totalGears && attempts < 120) {
      attempts++;
      // 既存の歯車から親を1つ選ぶ
      const parentIdx = Math.floor(p.random(this.gears.length));
      const parent = this.gears[parentIdx];

      const newRadius = p.random(32, 92) * scale;
      const newTeeth = Math.max(12, Math.round((newRadius / masterRadius) * 38));
      const placeAngle = p.random(p.TWO_PI);
      const dist = parent.radius + newRadius + 2 * scale; // 歯が噛み合う距離
      const nx = parent.x + Math.cos(placeAngle) * dist;
      const ny = parent.y + Math.sin(placeAngle) * dist;

      // 画面外に行きすぎないか＆他の歯車と重なりすぎないかチェック
      if (nx < w * 0.08 || nx > w * 0.92 || ny < h * 0.10 || ny > h * 0.90) continue;

      let overlap = false;
      for (let j = 0; j < this.gears.length; j++) {
        if (j === parentIdx) continue;
        const other = this.gears[j];
        const d = p.dist(nx, ny, other.x, other.y);
        if (d < (newRadius + other.radius) * 0.82) {
          overlap = true;
          break;
        }
      }
      if (overlap) continue;

      // 歯数比に応じて逆回転・角速度を決定
      const newDir = -parent.dir;
      const newSpeed = parent.speed * (parent.radius / newRadius);

      // 赤い歯車をメインにしつつ、白・黒鉄の歯車を織り交ぜてコントラストを作る
      const rnd = p.random();
      let style = 'crimson';
      if (rnd < 0.24) style = 'white_steel';
      else if (rnd < 0.40) style = 'dark_iron';

      const idx = this.gears.length;
      this.gears.push({
        x: nx,
        y: ny,
        radius: newRadius,
        teeth: newTeeth,
        angle: p.random(p.TWO_PI),
        speed: p.constrain(newSpeed, 0.2, 2.2),
        dir: newDir,
        style,
        spokes: newRadius > 60 * scale ? 4 : 3,
        hasInscription: newRadius > 58 * scale && idx % 2 === 1,
        inscriptionText: this.ringInscriptions[idx % this.ringInscriptions.length],
        depth: p.random(0.6, 1.4)
      });
    }

    // アンティーク時計の文字盤（2〜4基）を配置
    const clockPositions = [
      { x: masterX, y: masterY, r: masterRadius * 1.18, isWhite: false, speed: -0.35, hasRoman: true },
      {
        x: this.modeIndex === 1 ? w * 0.72 : cx + 145 * scale,
        y: this.modeIndex === 1 ? h * 0.34 : cy - 85 * scale,
        r: 78 * scale,
        isWhite: true,
        speed: 0.85,
        hasRoman: true
      },
      {
        x: cx - 155 * scale,
        y: cy + 95 * scale,
        r: 64 * scale,
        isWhite: false,
        speed: -0.65,
        hasRoman: false
      }
    ];

    for (let i = 0; i < clockPositions.length; i++) {
      const cp = clockPositions[i];
      this.clocks.push({
        x: cp.x,
        y: cp.y,
        radius: cp.r,
        angle: p.random(p.TWO_PI),
        speed: cp.speed,
        isWhite: cp.isWhite,
        hasRoman: cp.hasRoman,
        inscription: this.ringInscriptions[(i + 1) % this.ringInscriptions.length]
      });
    }
  }

  onPointerDown(x, y) {
    const p = this.p;
    const scale = Math.min(p.width, p.height) / 720;

    // クリックした位置に新しい深紅の歯車＆時計ダイヤルが噛み合って出現
    const radius = p.random(48, 86) * scale;
    this.spawnedGears.push({
      x,
      y,
      radius: 8,
      targetRadius: radius,
      teeth: Math.floor(p.random(16, 28)),
      angle: p.random(p.TWO_PI),
      speed: p.random(0.8, 1.8) * (p.random() < 0.5 ? 1 : -1),
      alpha: 255,
      isClock: p.random() < 0.5,
      inscription: this.ringInscriptions[Math.floor(p.random(this.ringInscriptions.length))]
    });

    if (this.spawnedGears.length > 6) {
      this.spawnedGears.shift();
    }

    // 機構全体に脱進機の強い衝撃（針の跳躍と赤いフラッシュ）を与える
    this.escapementImpulse = 1.8;
    this.crimsonPulse = 0.65;

    // クリック地点から赤い火花粒子が飛び散る
    for (let i = 0; i < 16; i++) {
      const ang = p.random(p.TWO_PI);
      const spd = p.random(1.2, 4.5);
      this.dustMotes.push({
        x,
        y,
        vx: Math.cos(ang) * spd,
        vy: Math.sin(ang) * spd,
        size: p.random(2.0, 4.5),
        alpha: 255,
        isRed: true,
        phase: p.random(p.TWO_PI),
        temporary: true
      });
    }
  }

  onPointerMove(x, y) {
    const p = this.p;
    this.pointerX = x;
    this.pointerY = y;

    if (p.frameCount % 2 === 0) {
      this.cursorTrail.push({
        x,
        y,
        size: p.random(8, 16),
        rot: p.random(p.TWO_PI),
        alpha: 190,
        isRed: p.random() < 0.65
      });
      if (this.cursorTrail.length > 22) {
        this.cursorTrail.shift();
      }
    }
  }

  draw(params) {
    const p = this.p;
    const w = p.width;
    const h = p.height;
    const motion = params ? params.motion : 0.5;
    const glow = params ? params.glow : 0.6;
    const scale = Math.min(w, h) / 720;

    const dt = Math.min(p.deltaTime || 16.67, 50) / 16.67;
    const speedMult = 0.4 + motion * 1.25;

    // 時計の秒針のようなカチッ、カチッという脱進機（エスケープメント）の緩急リズム
    const rawTick = (this.time * 2.5) % 1.0;
    const tickPulse = Math.pow(Math.sin(rawTick * p.PI), 4) * 0.35;
    const effectiveSpeed = speedMult * (1.0 + tickPulse + this.escapementImpulse);

    this.time += 0.016 * dt * effectiveSpeed;
    this.escapementImpulse = p.lerp(this.escapementImpulse, 0, 0.07 * dt);
    this.crimsonPulse = p.lerp(this.crimsonPulse, 0, 0.06 * dt);

    // 視差（パララックス）の滑らかな補間
    const targetPx = ((this.pointerX || w * 0.5) / w - 0.5) * 26;
    const targetPy = ((this.pointerY || h * 0.5) / h - 0.5) * 26;
    this.parallaxX = p.lerp(this.parallaxX, targetPx, 0.07);
    this.parallaxY = p.lerp(this.parallaxY, targetPy, 0.07);

    // =========================================================================
    // 1. 背景：暗闇の廃室とスポットライト光束（Mode 3 のみ深紅エクリプス背景）
    // =========================================================================
    this.drawDarkRoomAndSpotlight(w, h, scale, glow);

    // =========================================================================
    // 2. 画面を貫くドラフティング補助線（岩佐知昴氏の幾何学設計線）と直管蛍光灯
    // =========================================================================
    this.drawDraftingGridAndFluorescentTubes(w, h, scale, glow);

    // =========================================================================
    // 3. モード別のサブモチーフ（裸電球・『世』の外枠・黒電話と受話器）
    // =========================================================================
    this.drawModeSpecificMotifs(w, h, scale, glow);

    // =========================================================================
    // 4. メイン：噛み合って回転する深紅・白鋼・黒鉄の精密歯車ネットワーク
    // =========================================================================
    this.drawGearNetwork(w, h, scale, dt, effectiveSpeed, glow);

    // =========================================================================
    // 5. メイン：逆回転・交差するアンティーク時計の文字盤と針
    // =========================================================================
    this.drawAntiqueClocks(w, h, scale, dt, effectiveSpeed, glow);

    // =========================================================================
    // 6. クリックで生成されたインタラクティブ歯車＆時計波紋
    // =========================================================================
    this.updateAndDrawSpawnedGears(scale, dt, effectiveSpeed, glow);

    // =========================================================================
    // 7. 空間を舞うダスト・深紅の火花・カーソル軌跡
    // =========================================================================
    this.updateAndDrawDustAndSparks(w, h, dt, glow);
    this.updateAndDrawCursorTrail(dt);

    // =========================================================================
    // 8. 控えめで鋭利なタイトル刻印（画面隅のドラフティング署名）
    // =========================================================================
    this.drawSubtleCornerSignature(w, h, scale, glow);

    // =========================================================================
    // 9. ブラウン管走査線（CRTスキャンライン）と重厚なダークビネット
    // =========================================================================
    this.drawCRTScanlinesAndVignette(w, h);
  }

  /**
   * 1. 暗闇の廃室と斜めに差し込むスポットライト光束
   */
  drawDarkRoomAndSpotlight(w, h, scale, glow) {
    const p = this.p;
    const isEclipse = this.modeIndex === 3;

    if (isEclipse) {
      // 深紅エクリプスモード（MVの赤黒反転カットの空気感）
      const rBg = p.lerp(42, 95, this.crimsonPulse);
      p.background(rBg, 5, 9);
    } else {
      // 深いチャコールブラックに微かな赤の残響
      p.background(8 + this.crimsonPulse * 55, 9, 11);
    }

    p.push();
    p.translate(w * 0.5 - this.parallaxX * 0.3, h * 0.5 - this.parallaxY * 0.3);
    p.rotate(this.roomTilt);

    // 左上から右下へ斜めに差し込むスポットライトの光束
    p.noStroke();
    for (let i = 5; i >= 1; i--) {
      const spread = i * 52 * scale;
      if (isEclipse) {
        p.fill(185, 18, 32, (6 + glow * 8) * (6 - i) * 0.28);
      } else {
        p.fill(210, 220, 228, (5 + glow * 7) * (6 - i) * 0.25);
      }
      p.quad(
        -w * 0.65, -h * 0.65,
        -w * 0.15 + spread * 0.4, -h * 0.65,
        w * 0.65 + spread, h * 0.65,
        -w * 0.25 - spread, h * 0.65
      );
    }

    // 廃室の壁・床の淡い境界線
    p.stroke(isEclipse ? [220, 40, 55, 45] : [180, 192, 200, 32]);
    p.strokeWeight(1.0 * scale);
    p.line(-w * 0.7, h * 0.24, w * 0.7, h * 0.24);
    p.line(-w * 0.28, -h * 0.7, -w * 0.28, h * 0.7);
    p.pop();
  }

  /**
   * 2. ドラフティング補助線と明滅する直管蛍光灯
   */
  drawDraftingGridAndFluorescentTubes(w, h, scale, glow) {
    const p = this.p;
    p.push();

    // 歯車と時計の中心を結ぶ繊細な幾何学設計ライン（ドラフティングヘアライン）
    p.stroke(220, 228, 235, 38 * glow);
    p.strokeWeight(0.8 * scale);
    for (let i = 0; i < this.clocks.length; i++) {
      const ck = this.clocks[i];
      const cx = ck.x - this.parallaxX * 0.5;
      const cy = ck.y - this.parallaxY * 0.5;
      p.line(0, cy, w, cy);
      p.line(cx, 0, cx, h);
    }

    // 白く発光する直管蛍光灯
    for (let i = 0; i < this.fluorescentTubes.length; i++) {
      const t = this.fluorescentTubes[i];
      const flicker = 0.86 + 0.14 * Math.sin(this.time * 18.0 + t.flickerPhase);
      const x1 = t.x1 * w - this.parallaxX * 0.6;
      const y1 = t.y1 * h - this.parallaxY * 0.6;
      const x2 = t.x2 * w - this.parallaxX * 0.6;
      const y2 = t.y2 * h - this.parallaxY * 0.6;

      // 蛍光灯のハロー
      p.stroke(215, 235, 248, 28 * glow * flicker);
      p.strokeWeight(t.thickness * 4.2 * scale);
      p.line(x1, y1, x2, y2);

      p.stroke(235, 245, 252, 75 * glow * flicker);
      p.strokeWeight(t.thickness * 1.8 * scale);
      p.line(x1, y1, x2, y2);

      // 蛍光管の白い芯
      p.stroke(255, 255, 255, 195 * flicker);
      p.strokeWeight(t.thickness * 0.85 * scale);
      p.line(x1, y1, x2, y2);
    }
    p.pop();
  }

  /**
   * 3. モード別の象徴的サブモチーフ描画（再生成で切り替わる演出）
   * - Mode 1: 『世』の白いL字外枠（内部で赤い歯車が噛み合う）＋ 右側の明滅する裸電球
   * - Mode 2: 右下の黒電話と左上から吊り下がる受話器
   */
  drawModeSpecificMotifs(w, h, scale, glow) {
    const p = this.p;

    if (this.modeIndex === 1 && this.gears.length > 0) {
      // 主歯車を取り囲む『世』の白いL字部首フレーム（pandora_f2.jpg の象徴的構図）
      const mainG = this.gears[0];
      const wx = mainG.x - this.parallaxX * 0.7;
      const wy = mainG.y - this.parallaxY * 0.7;
      const r = mainG.radius * 1.15;

      p.push();
      p.noStroke();
      p.fill(245, 248, 252, 225);
      // 左へ突き出る水平線
      p.rect(wx - r * 1.45, wy - r * 0.45, r * 0.55, 11 * scale);
      // 左の垂直幹
      p.rect(wx - r * 1.02, wy - r * 0.95, 18 * scale, r * 1.95);
      // 下の水平ベース
      p.rect(wx - r * 1.02, wy + r * 0.88, r * 2.15, 11 * scale);
      p.pop();

      // 右側で明滅する白熱裸電球（pandora_f2.jpg 右側）
      this.drawIncandescentBulb(
        w * 0.78 - this.parallaxX * 0.9,
        h * 0.56 - this.parallaxY * 0.9,
        scale * 0.82,
        glow
      );
    } else if (this.modeIndex === 2) {
      // 左上からぶら下がる受話器と、右下の黒電話シルエット
      this.drawRotaryPhoneAndReceiver(w, h, scale, glow);
    }
  }

  /**
   * 4. 噛み合って回転する深紅・白鋼・黒鉄の精密歯車ネットワークの描画
   */
  drawGearNetwork(w, h, scale, dt, effectiveSpeed, glow) {
    const p = this.p;

    for (let i = 0; i < this.gears.length; i++) {
      const g = this.gears[i];
      const gx = g.x - this.parallaxX * g.depth;
      const gy = g.y - this.parallaxY * g.depth;

      // ポインタが近い歯車は共鳴して回転が速まり、赤く発光する
      const distToPointer = p.dist(this.pointerX, this.pointerY, gx, gy);
      const pointerBoost = distToPointer < g.radius * 1.8
        ? p.map(distToPointer, 0, g.radius * 1.8, 1.8, 0)
        : 0;

      g.angle += g.dir * g.speed * (0.012 * effectiveSpeed + pointerBoost * 0.025) * dt;

      // 歯車のカラー設定（印象的な深紅を主役に）
      let gearCol;
      let holeCol = this.modeIndex === 3 ? [38, 4, 8, 240] : [10, 12, 14, 240];

      if (g.style === 'crimson_main') {
        gearCol = [212 + pointerBoost * 35, 12, 26, 245];
      } else if (g.style === 'crimson') {
        gearCol = [186 + pointerBoost * 45, 14, 28, 232];
      } else if (g.style === 'white_steel') {
        gearCol = [240, 244, 248, 228];
      } else {
        // dark_iron (赤いリム付き黒鉄歯車)
        gearCol = [28, 32, 38, 235];
      }

      // 深紅の歯車の背後に淡い真紅のグロウを灯す
      if (g.style.startsWith('crimson') || pointerBoost > 0.2) {
        p.push();
        p.noStroke();
        p.fill(210, 12, 26, (22 + pointerBoost * 35) * glow);
        p.circle(gx, gy, g.radius * 2.35);
        p.pop();
      }

      this.drawDetailedGear(
        gx,
        gy,
        g.radius,
        g.teeth,
        g.angle,
        gearCol,
        holeCol,
        g.spokes,
        g.style === 'dark_iron'
      );

      // 大歯車の外周に刻まれた繊細な円環歌詞（主張しすぎない時計刻印）
      if (g.hasInscription) {
        const textCol = g.style === 'white_steel'
          ? [235, 240, 245, 165]
          : [225, 38, 52, 185];
        this.drawCircularInscription(
          g.inscriptionText,
          gx,
          gy,
          g.radius + 13 * scale,
          -g.angle * 0.65,
          Math.max(8.5, 10.5 * scale),
          textCol
        );
      }
    }
  }

  /**
   * 5. 逆回転・交差するアンティーク時計の文字盤と針の描画
   */
  drawAntiqueClocks(w, h, scale, dt, effectiveSpeed, glow) {
    for (let i = 0; i < this.clocks.length; i++) {
      const ck = this.clocks[i];
      const cx = ck.x - this.parallaxX * 0.85;
      const cy = ck.y - this.parallaxY * 0.85;

      ck.angle += ck.speed * 0.01 * effectiveSpeed * dt;

      this.drawSingleAntiqueClock(
        cx,
        cy,
        ck.radius,
        ck.angle,
        ck.isWhite,
        ck.hasRoman,
        ck.inscription,
        scale,
        glow
      );
    }
  }

  /**
   * 精密な機械式歯車（ギア）を描画するメソッド
   */
  drawDetailedGear(x, y, radius, teethCount, rotation, colorRGBA, holeRGBA, spokes, hasCrimsonRim) {
    const p = this.p;
    p.push();
    p.translate(x, y);
    p.rotate(rotation);

    const r = colorRGBA[0];
    const g = colorRGBA[1];
    const b = colorRGBA[2];
    const a = colorRGBA[3];

    // 1. 外周の台形ティース（歯）
    p.noStroke();
    p.fill(r, g, b, a);
    const step = p.TWO_PI / teethCount;
    const toothDepth = Math.max(3.5, radius * 0.115);

    p.beginShape();
    for (let i = 0; i < teethCount; i++) {
      const a1 = i * step;
      const a2 = a1 + step * 0.20;
      const a3 = a1 + step * 0.52;
      const a4 = a1 + step * 0.72;
      p.vertex(Math.cos(a1) * radius, Math.sin(a1) * radius);
      p.vertex(Math.cos(a2) * (radius + toothDepth), Math.sin(a2) * (radius + toothDepth));
      p.vertex(Math.cos(a3) * (radius + toothDepth), Math.sin(a3) * (radius + toothDepth));
      p.vertex(Math.cos(a4) * radius, Math.sin(a4) * radius);
    }
    p.endShape(p.CLOSE);

    // 2. 内部くり抜きリング
    p.fill(holeRGBA[0], holeRGBA[1], holeRGBA[2], holeRGBA[3]);
    p.circle(0, 0, radius * 1.64);

    // 3. 内輪の二重精密リングと目盛りティック
    p.noFill();
    if (hasCrimsonRim) {
      p.stroke(212, 16, 30, 220);
    } else {
      p.stroke(r, g, b, a);
    }
    p.strokeWeight(Math.max(1.5, radius * 0.065));
    p.circle(0, 0, radius * 1.44);

    p.strokeWeight(Math.max(1.0, radius * 0.035));
    p.circle(0, 0, radius * 1.14);

    // 4. スポーク（支柱）と飾り穴
    p.strokeWeight(Math.max(2.0, radius * 0.08));
    for (let s = 0; s < spokes; s++) {
      const sa = (p.TWO_PI / spokes) * s;
      p.line(0, 0, Math.cos(sa) * radius * 0.72, Math.sin(sa) * radius * 0.72);
    }

    // 5. 中心ハブと軸穴
    p.noStroke();
    p.fill(hasCrimsonRim ? [212, 16, 30, 235] : [r, g, b, a]);
    p.circle(0, 0, radius * 0.36);
    p.fill(holeRGBA[0], holeRGBA[1], holeRGBA[2], holeRGBA[3]);
    p.circle(0, 0, radius * 0.15);

    p.pop();
  }

  /**
   * アンティーク時計の文字盤（ローマ数字・60分目盛り・長針/短針/赤い秒針）を描画
   */
  drawSingleAntiqueClock(x, y, radius, angle, isWhite, hasRoman, inscription, scale, glow) {
    const p = this.p;
    p.push();
    p.translate(x, y);

    // 時計文字盤のベースと外枠リング
    p.push();
    p.rotate(angle);

    if (isWhite) {
      p.fill(238, 242, 245, 42);
      p.stroke(240, 244, 248, 215);
    } else {
      p.fill(14, 16, 20, 125);
      p.stroke(212, 18, 32, 225);
    }
    p.strokeWeight(2.2 * scale);
    p.circle(0, 0, radius * 2.0);

    // 二重ベゼルと60分目盛り
    p.noFill();
    p.strokeWeight(1.1 * scale);
    p.circle(0, 0, radius * 1.88);
    p.circle(0, 0, radius * 1.48);

    const tickColor = isWhite ? [240, 244, 248, 210] : [220, 28, 42, 215];
    p.stroke(tickColor[0], tickColor[1], tickColor[2], tickColor[3]);
    for (let m = 0; m < 60; m++) {
      const a = (p.TWO_PI / 60) * m;
      const isHour = m % 5 === 0;
      p.strokeWeight((isHour ? 2.2 : 0.9) * scale);
      const rInner = isHour ? radius * 0.76 : radius * 0.86;
      const rOuter = radius * 0.93;
      p.line(Math.cos(a) * rInner, Math.sin(a) * rInner, Math.cos(a) * rOuter, Math.sin(a) * rOuter);
    }

    // ローマ数字の刻印（XII, I, II, ...）
    if (hasRoman) {
      const romans = ['XII', 'I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X', 'XI'];
      p.noStroke();
      p.fill(isWhite ? [245, 248, 252, 215] : [235, 225, 228, 205]);
      p.textFont("'Cinzel', 'Cormorant Garamond', 'Noto Serif JP', serif");
      p.textAlign(p.CENTER, p.CENTER);
      p.textSize(Math.max(8, radius * 0.13));

      for (let hIdx = 0; hIdx < 12; hIdx++) {
        const a = (p.TWO_PI / 12) * hIdx - p.HALF_PI;
        const rx = Math.cos(a) * radius * 0.64;
        const ry = Math.sin(a) * radius * 0.64;
        p.push();
        p.translate(rx, ry);
        p.rotate(a + p.HALF_PI);
        p.text(romans[hIdx], 0, 0);
        p.pop();
      }
    }
    p.pop();

    // 長針・短針・深紅の秒針（それぞれ異なる速度で逆回転・順回転）
    const hourAngle = -angle * 1.4 + this.escapementImpulse * 0.3;
    const minAngle = angle * 3.8 - this.escapementImpulse * 0.8;
    const secAngle = -this.time * 2.6;

    // 短針（時針）
    p.push();
    p.rotate(hourAngle);
    p.stroke(245, 248, 252, 235);
    p.strokeWeight(3.2 * scale);
    p.line(-radius * 0.12, 0, radius * 0.50, 0);
    // アンティーク時計特有のスペード針装飾
    p.noFill();
    p.strokeWeight(1.8 * scale);
    p.circle(radius * 0.34, 0, radius * 0.12);
    p.pop();

    // 長針（分針）
    p.push();
    p.rotate(minAngle);
    p.stroke(245, 248, 252, 240);
    p.strokeWeight(2.2 * scale);
    p.line(-radius * 0.16, 0, radius * 0.78, 0);
    p.pop();

    // 深紅の秒針（精巧な音の罠）
    p.push();
    p.rotate(secAngle);
    p.stroke(220, 16, 32, 245);
    p.strokeWeight(1.4 * scale);
    p.line(-radius * 0.22, 0, radius * 0.88, 0);
    p.fill(220, 16, 32, 245);
    p.noStroke();
    p.circle(0, 0, 7 * scale);
    p.pop();

    // 時計外周の繊細な円環刻印
    if (inscription) {
      this.drawCircularInscription(
        inscription,
        0,
        0,
        radius + 11 * scale,
        angle * 0.5,
        Math.max(8, 9.5 * scale),
        isWhite ? [230, 236, 242, 155] : [216, 32, 46, 175]
      );
    }

    p.pop();
  }

  /**
   * クリックで出現するインタラクティブ歯車＆時計波紋の更新・描画
   */
  updateAndDrawSpawnedGears(scale, dt, effectiveSpeed, glow) {
    const p = this.p;
    for (let i = this.spawnedGears.length - 1; i >= 0; i--) {
      const sg = this.spawnedGears[i];
      sg.radius = p.lerp(sg.radius, sg.targetRadius, 0.12 * dt);
      sg.angle += sg.speed * 0.02 * effectiveSpeed * dt;
      sg.alpha -= 1.8 * dt;

      if (sg.alpha <= 0) {
        this.spawnedGears.splice(i, 1);
        continue;
      }

      const fade = sg.alpha / 255;
      const col = [216, 14, 28, sg.alpha];
      const hole = [10, 12, 14, sg.alpha * 0.9];

      this.drawDetailedGear(sg.x, sg.y, sg.radius, sg.teeth, sg.angle, col, hole, 4, false);

      if (sg.isClock) {
        p.push();
        p.translate(sg.x, sg.y);
        p.noFill();
        p.stroke(245, 248, 252, sg.alpha * 0.85);
        p.strokeWeight(1.4 * scale);
        p.circle(0, 0, sg.radius * 2.25);
        p.pop();
      }

      this.drawCircularInscription(
        sg.inscription,
        sg.x,
        sg.y,
        sg.radius + 12 * scale,
        -sg.angle * 0.8,
        9.5 * scale,
        [245, 248, 252, sg.alpha * 0.85]
      );
    }
  }

  /**
   * 歯車・時計の外周に沿った繊細な円環刻印ヘルパー
   */
  drawCircularInscription(str, cx, cy, radius, baseAngle, fontSize, colorRGBA) {
    const p = this.p;
    p.push();
    p.translate(cx, cy);
    p.textFont("'Noto Serif JP', 'Yu Mincho', serif");
    p.textAlign(p.CENTER, p.CENTER);
    p.textSize(fontSize);
    p.noStroke();

    const angleStep = p.TWO_PI / Math.max(24, str.length);
    for (let i = 0; i < str.length; i++) {
      const ch = str[i];
      if (ch === ' ') continue;
      const theta = baseAngle + i * angleStep;
      p.push();
      p.translate(Math.cos(theta) * radius, Math.sin(theta) * radius);
      p.rotate(theta + p.HALF_PI);
      if ('愛罠災厄壊世踊'.includes(ch)) {
        p.fill(228, 18, 34, Math.min(255, colorRGBA[3] * 1.25));
      } else {
        p.fill(colorRGBA[0], colorRGBA[1], colorRGBA[2], colorRGBA[3]);
      }
      p.text(ch, 0, 0);
      p.pop();
    }
    p.pop();
  }

  /**
   * 激しく明滅する白熱裸電球（Mode 1 のサブモチーフ）
   */
  drawIncandescentBulb(x, y, scale, glow) {
    const p = this.p;
    const flicker = 0.82 + 0.18 * Math.sin(this.time * 17.0) * Math.cos(this.time * 6.7);

    p.push();
    p.translate(x, y);
    p.rotate(0.42);

    // フィラメント周辺の白色ハロー
    p.noStroke();
    for (let g = 5; g >= 1; g--) {
      const rad = g * 48 * scale * flicker;
      p.fill(245, 250, 255, (12 + glow * 14) * (6 - g) * 0.22);
      p.ellipse(0, 45 * scale, rad, rad * 1.15);
    }

    // ガラス球の輪郭
    p.stroke(235, 242, 248, 195);
    p.strokeWeight(2.2 * scale);
    p.fill(210, 222, 230, 32);
    p.beginShape();
    p.vertex(-42 * scale, 65 * scale);
    p.bezierVertex(-52 * scale, 18 * scale, -110 * scale, -35 * scale, -96 * scale, -120 * scale);
    p.bezierVertex(-82 * scale, -198 * scale, 82 * scale, -198 * scale, 96 * scale, -120 * scale);
    p.bezierVertex(110 * scale, -35 * scale, 52 * scale, 18 * scale, 42 * scale, 65 * scale);
    p.endShape(p.CLOSE);

    // 内部フィラメントの輝き
    p.stroke(255, 255, 255, 245);
    p.strokeWeight(2.2 * scale * flicker);
    p.noFill();
    p.beginShape();
    p.vertex(-14 * scale, 60 * scale);
    p.vertex(-38 * scale, -55 * scale);
    p.vertex(-22 * scale, -82 * scale);
    p.vertex(0, -64 * scale);
    p.vertex(22 * scale, -82 * scale);
    p.vertex(38 * scale, -55 * scale);
    p.vertex(14 * scale, 60 * scale);
    p.endShape();

    // ソケット口金
    p.fill(230, 236, 242, 235);
    p.stroke(80, 88, 96, 210);
    p.strokeWeight(1.5 * scale);
    p.rectMode(p.CENTER);
    for (let s = 0; s < 4; s++) {
      p.rect(0, (76 + s * 12) * scale, (76 - s * 6) * scale, 10 * scale, 4 * scale);
    }
    p.pop();
  }

  /**
   * 黒電話と宙に浮く受話器（Mode 2 のサブモチーフ）
   */
  drawRotaryPhoneAndReceiver(w, h, scale, glow) {
    const p = this.p;
    p.push();

    // 左上から吊り下がる受話器
    const rx = w * 0.16 - this.parallaxX * 0.8;
    const ry = h * 0.24 - this.parallaxY * 0.8;
    const sway = Math.sin(this.time * 1.9) * 0.08;

    p.stroke(215, 222, 228, 175);
    p.strokeWeight(2.0 * scale);
    p.noFill();
    p.bezier(w * 0.08, 0, w * 0.12, h * 0.10, rx - 15 * scale, ry - 45 * scale, rx, ry - 25 * scale);

    p.push();
    p.translate(rx, ry);
    p.rotate(0.55 + sway);
    p.fill(14, 16, 20, 240);
    p.stroke(215, 222, 228, 185);
    p.strokeWeight(1.4 * scale);
    p.rectMode(p.CENTER);
    p.rect(0, 0, 18 * scale, 78 * scale, 8 * scale);
    p.ellipse(-6 * scale, -38 * scale, 34 * scale, 24 * scale);
    p.ellipse(-6 * scale, 38 * scale, 34 * scale, 24 * scale);
    p.pop();

    // 右下の黒電話本体（ダイヤルが歯車と共鳴して回転）
    const px = w * 0.82 - this.parallaxX * 0.9;
    const py = h * 0.80 - this.parallaxY * 0.9;
    p.push();
    p.translate(px, py);
    p.rotate(this.roomTilt * 0.5);
    p.fill(12, 14, 18, 245);
    p.stroke(210, 218, 225, 165);
    p.strokeWeight(1.4 * scale);
    p.beginShape();
    p.vertex(-72 * scale, 42 * scale);
    p.vertex(72 * scale, 42 * scale);
    p.vertex(52 * scale, -22 * scale);
    p.vertex(-52 * scale, -22 * scale);
    p.endShape(p.CLOSE);

    // 回転する白いダイヤル盤
    p.fill(240, 244, 248, 235);
    p.circle(0, 12 * scale, 56 * scale);
    p.fill(16, 18, 22, 240);
    for (let k = 0; k < 8; k++) {
      const a = this.time * 1.2 + (p.TWO_PI / 10) * k;
      p.circle(Math.cos(a) * 18 * scale, 12 * scale + Math.sin(a) * 18 * scale, 7 * scale);
    }
    p.pop();

    p.pop();
  }

  /**
   * 空間を漂う埃と深紅の火花粒子の更新・描画
   */
  updateAndDrawDustAndSparks(w, h, dt, glow) {
    const p = this.p;
    p.push();
    p.noStroke();
    for (let i = this.dustMotes.length - 1; i >= 0; i--) {
      const d = this.dustMotes[i];
      d.x += (d.vx + Math.sin(this.time + d.phase) * 0.18) * dt;
      d.y += d.vy * dt;

      if (d.temporary) {
        d.vx *= 0.95;
        d.vy *= 0.95;
        d.alpha -= 4.5 * dt;
        if (d.alpha <= 0) {
          this.dustMotes.splice(i, 1);
          continue;
        }
      } else {
        if (d.y < -10) d.y = h + 10;
        if (d.x < -10) d.x = w + 10;
        if (d.x > w + 10) d.x = -10;
      }

      const twinkle = 0.55 + 0.45 * Math.sin(this.time * 2.8 + d.phase);
      if (d.isRed) {
        p.fill(225, 22, 38, d.alpha * twinkle);
      } else {
        p.fill(225, 232, 238, d.alpha * twinkle * glow);
      }
      p.circle(d.x, d.y, d.size);
    }
    p.pop();
  }

  /**
   * カーソル移動軌跡の小さな歯車・ドラフティング十字の描画
   */
  updateAndDrawCursorTrail(dt) {
    const p = this.p;
    if (this.cursorTrail.length === 0) return;

    p.push();
    p.noFill();
    for (let i = this.cursorTrail.length - 1; i >= 0; i--) {
      const tr = this.cursorTrail[i];
      tr.alpha -= 7.5 * dt;
      tr.rot += 0.08 * dt;
      if (tr.alpha <= 0) {
        this.cursorTrail.splice(i, 1);
        continue;
      }

      p.push();
      p.translate(tr.x, tr.y);
      p.rotate(tr.rot);
      p.stroke(tr.isRed ? [220, 18, 34, tr.alpha] : [235, 242, 248, tr.alpha]);
      p.strokeWeight(1.2);
      p.circle(0, 0, tr.size);
      p.line(-tr.size * 0.75, 0, tr.size * 0.75, 0);
      p.line(0, -tr.size * 0.75, 0, tr.size * 0.75);
      p.pop();
    }
    p.pop();
  }

  /**
   * 画面左下の控えめなドラフティング署名（アートを邪魔しない設計図表記）
   */
  drawSubtleCornerSignature(w, h, scale, glow) {
    const p = this.p;
    p.push();
    const sx = w * 0.05;
    const sy = h * 0.92;

    p.stroke(220, 228, 235, 95);
    p.strokeWeight(0.9 * scale);
    p.line(sx, sy - 14 * scale, sx + 175 * scale, sy - 14 * scale);

    p.noStroke();
    p.fill(235, 240, 245, 165);
    p.textFont("'Noto Serif JP', 'Yu Mincho', serif");
    p.textAlign(p.LEFT, p.CENTER);
    p.textSize(11 * scale);
    p.text('パ ン ド ラ コ ー ル   —   P A N D O R A   C A L L', sx, sy);
    p.pop();
  }

  /**
   * MV全編を覆うブラウン管走査線（スキャンライン）と重厚なダークビネット
   */
  drawCRTScanlinesAndVignette(w, h) {
    const p = this.p;
    p.push();
    p.stroke(0, 0, 0, 38);
    p.strokeWeight(1);
    for (let y = 0; y < h; y += 3) {
      p.line(0, y, w, y);
    }

    p.noFill();
    const layers = 9;
    for (let i = 1; i <= layers; i++) {
      const t = i / layers;
      p.stroke(3, 4, 6, t * 26);
      p.strokeWeight((layers - i + 1) * 5);
      p.rect(0, 0, w, h);
    }
    p.pop();
  }
}
